import { List, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { NavLink, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Icon from '@mui/material/Icon';
import ListItem from '@mui/material/ListItem';
import PropTypes from 'prop-types';

import { AvatarImage } from '../Avatar';
import { navigateToBaseUrl } from '../../utils/navigator';
import { SUPER_ADMIN, ADMIN, EMPLOYEE } from '../../constants/roles';
import * as navbarIcons from './NavbarIcons';
import LogoutDialog from '../Logout';
import useWorkspace from '../../context/WorkspaceContext';
import WorkspaceDropdown from './workspace-dropdown';

const useStyles = makeStyles({
  logo: {
    maxWidth: '40px',
    width: '100%',
  },
  icons: {
    maxWidth: '20px',
    width: '100%',
  },
  profile: {
    width: '36px',
    height: '40px',
  },
});

const LogoutIconPropTypes = {
  activeNav: PropTypes.string,
  className: PropTypes.string.isRequired,
  handleDialog: PropTypes.func.isRequired,
};

const LogoutIcon = ({ activeNav, className, handleDialog }) => (
  <Icon
    className="logout-ic"
    sx={{ height: 'auto', width: 'auto', cursor: 'pointer' }}
    onClick={handleDialog}
  >
    <img alt="settings" className={className} src={activeNav === '/logout' ? navbarIcons.logout : navbarIcons.logout} draggable="false" />
  </Icon>
);

const PermanentDrawerLeft = () => {
  // init
  const { userPermissions, workspaceProfile } = useWorkspace();
  const location = useLocation();

  // constants
  const classes = useStyles();
  const menus = useMemo(() => [
    {
      to: '/projects',
      name: 'Project List',
      className: classes.icons,
      draggable: 'false',
      click: navbarIcons.projects,
      afterClick: navbarIcons.projects,
      isAllowed: [SUPER_ADMIN, ADMIN, EMPLOYEE],
    },
    {
      to: '/users',
      name: 'Users',
      className: classes.icons,
      draggable: 'false',
      click: navbarIcons.teams,
      afterClick: navbarIcons.teams,
      isAllowed: [SUPER_ADMIN, ADMIN],
    },
    {
      to: '/settings',
      name: 'Edit Workspace',
      className: classes.icons,
      draggable: 'false',
      click: navbarIcons.settings,
      afterClick: navbarIcons.settings,
      isAllowed: [SUPER_ADMIN, ADMIN],
    },
  ], [classes]);

  // states
  const [activeNav, setActiveNav] = useState(location.pathname);
  const [openDialog, setOpenDialog] = useState(false);

  const handleDialog = () => {
    setOpenDialog(!openDialog);
  };

  useEffect(() => {
    setActiveNav(location.pathname);
  }, [location.pathname]);

  const logoutHandler = () => {
    setActiveNav('/logout');
  };

  return (
    <Drawer className="sidebar-nav-main" variant="permanent" anchor="left">
      <WorkspaceDropdown />
      <List className="sidebar-nav">
        {menus.map((menu) => {
          if (Object.values(menu.isAllowed).includes(userPermissions?.roleId || EMPLOYEE)) {
            return (
              <ListItem key={menu.name}>
                <NavLink
                  className={activeNav === menu.to ? 'active' : ''}
                  to={menu.to}
                >
                  {menu.afterClick ? (
                    <img
                      alt={menu.name}
                      className={menu.className}
                      src={activeNav === menu.to ? menu.click : menu.afterClick}
                      draggable={menu.draggable}
                    />
                  ) : (
                    <img
                      alt={menu.name}
                      className={classes.logo}
                      src={menu.imgSrc}
                      draggable={menu.draggable}
                    />
                  )}
                </NavLink>
              </ListItem>
            );
          }
          return '';
        })}
      </List>

      <List className="sidebar-nav-bottom" sx={{ marginTop: 'auto' }}>
        <ListItem onClick={logoutHandler}>
          <LogoutDialog
            handleDialog={handleDialog}
            openDialog={openDialog}
            component={<LogoutIcon activeNav={activeNav} handleDialog={handleDialog} className={classes.icons} />}
            setOpenDialog={setOpenDialog}
            onLogoutSuccess={() => navigateToBaseUrl()}
          />
        </ListItem>
        <ListItem className={activeNav === '/profile' ? 'active' : ''}>
          <NavLink to="/profile" draggable="false">
            {
              workspaceProfile
                ? <AvatarImage src={workspaceProfile?.logo} alt="Profile" name={workspaceProfile?.displayName} fontSize={20} fontWeight={500} width={45} height={45} />
                : <Skeleton sx={{ bgcolor: 'white' }} animation="wave" variant="circular" width={45} height={45} />
            }
          </NavLink>
        </ListItem>
      </List>
    </Drawer>
  );
};

LogoutIcon.propTypes = LogoutIconPropTypes;

export default PermanentDrawerLeft;
